import * as XLSX from "xlsx-js-style";

const GRUPOS_PLANTILLA = [
  { label: "Insumo del Sistema", color: "1890FF", columnas: ["Clave Insumo", "Descripción Insumo"] },
  {
    label: "Datos para el Cliente",
    color: "52C41A",
    columnas: ["Nombre para el Cliente", "Clave Cliente", "Precio Unitario", "Presentación", "Unidad"],
  },
];
const COLOR_REFERENCIA = "595959";

export const COLUMNAS_PLANTILLA = GRUPOS_PLANTILLA.flatMap((grupo) => grupo.columnas);

export const normalizar = (valor) => String(valor ?? "").trim().toUpperCase();

export const formatoUnidad = (unidad) =>
  unidad?.unidadSat?.c_ClaveUnidad ? `${unidad.unidadSat.c_ClaveUnidad} - ${unidad.nombre}` : "";

const aclararColor = (hex, factor) => {
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const mezclar = (c) => Math.round(c + (255 - c) * factor);
  const aHex = (c) => c.toString(16).padStart(2, "0").toUpperCase();
  return `${aHex(mezclar(r))}${aHex(mezclar(g))}${aHex(mezclar(b))}`;
};
const BORDE_FINO = {
  top: { style: "thin", color: { rgb: "C0C0C0" } },
  bottom: { style: "thin", color: { rgb: "C0C0C0" } },
  left: { style: "thin", color: { rgb: "C0C0C0" } },
  right: { style: "thin", color: { rgb: "C0C0C0" } },
};
const estiloBannerGrupo = (color) => ({
  fill: { fgColor: { rgb: color } },
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Arial" },
  alignment: { horizontal: "center", vertical: "center" },
  border: BORDE_FINO,
});
const estiloEncabezadoColumna = (color) => ({
  fill: { fgColor: { rgb: aclararColor(color, 0.75) } },
  font: { bold: true, sz: 10, name: "Arial" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: BORDE_FINO,
});
const pintarEncabezadoReferencia = (hoja, columnas, fila = 0) => {
  columnas.forEach((_, indice) => {
    const cellRef = XLSX.utils.encode_cell({ r: fila, c: indice });
    if (hoja[cellRef]) hoja[cellRef].s = estiloBannerGrupo(COLOR_REFERENCIA);
  });
};

export const ALIAS_CLAVE_INSUMO = ["CLAVE INSUMO", "CLAVE SISTEMA", "CLAVE DEL INSUMO", "CODIGO", "CÓDIGO"];

export const obtenerValorColumna = (fila, alias) => {
  const encabezados = Object.keys(fila);
  for (const texto of alias) {
    const encabezado = encabezados.find((k) => normalizar(k) === texto);
    if (encabezado !== undefined) return fila[encabezado];
  }
  return undefined;
};

// Ya no existen los estados "YA_CONFIGURADO" ni "DUPLICADO": un insumo puede tener varias
// relaciones con el mismo cliente, así que repetirlo no descarta el renglón. El aviso de
// repetido lo calculan las pantallas en vivo (ver avisoRepetidoInsumoCliente), porque el
// usuario puede cambiar el insumo de un renglón después de leer el archivo.
export const ETIQUETAS_ESTADO_CLAVE = {
  NO_ENCONTRADO: { texto: "No encontrado", color: "red" },
  AMBIGUO: { texto: "Ambiguo", color: "orange" },
};

// identidad de un renglón = la equivalencia si la hay, si no el insumo (es lo que devuelve
// insumosCliente/selectInsumoIdsCliente)
export const identidadInsumoCliente = (fila) =>
  fila && (fila.equivalenciaId || fila.insumoId) ? String(fila.equivalenciaId || fila.insumoId) : "";

// Map<identidad, cuántos renglones vivos la usan>, para no recorrer la tabla por renglón
export const contarIdentidades = (filas) => {
  const conteo = new Map();
  (filas || []).forEach((fila) => {
    const identidad = identidadInsumoCliente(fila);
    if (identidad) conteo.set(identidad, (conteo.get(identidad) || 0) + 1);
  });
  return conteo;
};

// El texto del aviso, o null si ese insumo no se repite
export const avisoRepetidoInsumoCliente = (identidad, yaConfigurados, conteoIdentidades) => {
  if (!identidad) return null;
  if (yaConfigurados?.has(identidad)) return "Ya tiene relación con este cliente";
  if ((conteoIdentidades?.get(identidad) || 0) > 1) return "Repetido en esta captura";
  return null;
};

export const descargarPlantillaInsumosCliente = (catalogo, unidades) => {
  const filaGrupos = GRUPOS_PLANTILLA.flatMap((grupo) => grupo.columnas.map(() => grupo.label));
  const hoja = XLSX.utils.aoa_to_sheet([filaGrupos, COLUMNAS_PLANTILLA]);

  const merges = [];
  let indiceColumna = 0;
  GRUPOS_PLANTILLA.forEach((grupo) => {
    if (grupo.columnas.length > 1) {
      merges.push({
        s: { r: 0, c: indiceColumna },
        e: { r: 0, c: indiceColumna + grupo.columnas.length - 1 },
      });
    }
    grupo.columnas.forEach(() => {
      const cellGrupo = XLSX.utils.encode_cell({ r: 0, c: indiceColumna });
      const cellCol = XLSX.utils.encode_cell({ r: 1, c: indiceColumna });
      if (hoja[cellGrupo]) hoja[cellGrupo].s = estiloBannerGrupo(grupo.color);
      if (hoja[cellCol]) hoja[cellCol].s = estiloEncabezadoColumna(grupo.color);
      indiceColumna += 1;
    });
  });
  hoja["!merges"] = merges;
  hoja["!cols"] = COLUMNAS_PLANTILLA.map((titulo) => ({ wch: Math.max(titulo.length + 2, 16) }));
  hoja["!rows"] = [{ hpt: 22 }, { hpt: 28 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, hoja, "Insumos");

  if (Array.isArray(catalogo) && catalogo.length > 0) {
    const encabezadosCatalogo = ["Clave", "Descripción", "Unidad"];
    const hojaCatalogo = XLSX.utils.aoa_to_sheet([
      encabezadosCatalogo,
      ...catalogo.map((item) => [item.clave || "", item.descripcion || "", item.unidad || ""]),
    ]);
    pintarEncabezadoReferencia(hojaCatalogo, encabezadosCatalogo);
    hojaCatalogo["!cols"] = [{ wch: 20 }, { wch: 50 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, hojaCatalogo, "Catalogo Insumos");
  }

  if (Array.isArray(unidades) && unidades.length > 0) {
    const hojaUnidades = XLSX.utils.aoa_to_sheet([["Unidad"], ...unidades.map((texto) => [texto])]);
    pintarEncabezadoReferencia(hojaUnidades, ["Unidad"]);
    hojaUnidades["!cols"] = [{ wch: 30 }];
    XLSX.utils.book_append_sheet(workbook, hojaUnidades, "Catalogo Unidad");
  }

  XLSX.writeFile(workbook, "plantillaInsumosPorCliente.xlsx");
};

export const esArchivoExcel = (file) =>
  file?.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const leerFilasExcelInsumosCliente = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.onloadend = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const filasExcel = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[workbook.SheetNames[0]], { range: 1 });

        const filas = filasExcel
          .map((fila) => {
            const claveInsumo = obtenerValorColumna(fila, ALIAS_CLAVE_INSUMO);
            return {
              claveInsumo: claveInsumo === undefined || claveInsumo === null ? "" : String(claveInsumo).trim(),
              descripcionInsumo: fila["Descripción Insumo"] ? String(fila["Descripción Insumo"]).trim() : "",
              nombreAlternativo: fila["Nombre para el Cliente"] ? String(fila["Nombre para el Cliente"]).trim() : "",
              clave: fila["Clave Cliente"] ? String(fila["Clave Cliente"]).trim() : "",
              presentacion: fila["Presentación"] ? String(fila["Presentación"]).trim() : "",
              precio: fila["Precio Unitario"] !== undefined && fila["Precio Unitario"] !== null && fila["Precio Unitario"] !== "" ? Number(fila["Precio Unitario"].toString().replace(/\$\s?|(,*)/g, "")) : undefined,
              unidadTexto: fila["Unidad"] ? String(fila["Unidad"]).trim() : "",
              insumoId: undefined,
              unidadId: undefined,
            };
          })
          .filter((fila) => fila.nombreAlternativo);

        resolve(filas);
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });

export const resolverUnidadesDeFilas = (filas, unidadesPorTexto) =>
  filas.map((fila) => {
    if (!fila.unidadTexto) return fila;
    const unidadId = unidadesPorTexto?.get(normalizar(fila.unidadTexto));
    return unidadId ? { ...fila, unidadId } : fila;
  });

export const aplicarResultadosClaves = (filas, resultados) =>
  filas.map((fila, indice) => {
    if (!fila.claveInsumo) return fila;

    const resultado = resultados[indice];
    if (!resultado || resultado.estado !== "ENCONTRADO") {
      return { ...fila, estadoClave: resultado?.estado || "NO_ENCONTRADO", candidatos: resultado?.candidatos };
    }

    const insumo = resultado.insumo;
    const insumoId = String(insumo._id);
    const equivalenciaId = insumo.equivalenciaId ? String(insumo.equivalenciaId) : undefined;

    // Un insumo que el cliente ya tiene (o que viene dos veces en el archivo) se resuelve
    // igual que cualquier otro: repetirlo es legítimo y sólo se avisa en la tabla
    return {
      ...fila,
      estadoClave: undefined,
      insumoId,
      equivalenciaId,
      claveInterna: insumo.clave,
      descripcion: insumo.descripcion,
      unidadId: fila.unidadId || insumo.unidadId,
      stringBuscar: [insumo.clave, insumo.descripcion, insumo.unidadNombre].filter(Boolean).join(" - "),
      clave: fila.clave || insumo.clave || "",
    };
  });
